import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { StepHeader } from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useLeadPlacesAutocomplete } from '@/hooks/api/useLeadOnboarding'
import { SearchMdIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import type { PlacePrediction } from '@/types/onboarding'

interface PlaceSearchStepProps {
  onNext: () => void
  onBack: () => void
  onManualEntry: () => void
}

export function PlaceSearchStep({ onNext, onBack, onManualEntry }: PlaceSearchStepProps) {
  const { t } = useTranslation()
  const { setPlaceDetails, setManualEntry, leadId } = useOnboardingStore()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedPlace, setSelectedPlace] = useState<PlacePrediction | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  // Debounce search query (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 500)
    return () => clearTimeout(timer)
  }, [query])

  const { data: placesData, isLoading } = useLeadPlacesAutocomplete(debouncedQuery, leadId, debouncedQuery.length >= 3)
  
  const predictions = placesData?.predictions || []
  // Total items = predictions + 1 for "Fill in manually" option
  const totalItems = predictions.length + 1

  // Reset highlighted index when predictions change
  useEffect(() => {
    setHighlightedIndex(0)
  }, [predictions.length])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsDropdownOpen(false)
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])
  
  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex < predictions.length) {
      const items = listRef.current.querySelectorAll('li')
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, predictions.length])

  const handleSelectPlace = (place: PlacePrediction) => {
    setSelectedPlace(place)
    setQuery(place.structured_formatting.main_text)
    setIsDropdownOpen(false)
    // Automatically continue when a place is selected
    setManualEntry(false)
    setPlaceDetails({
      name: place.structured_formatting.main_text,
      address: place.structured_formatting.secondary_text,
      phone: '',
      website: '',
      google_place_id: place.place_id,
    })
    onNext()
  }

  const handleManualEntry = () => {
    setManualEntry(true)
    setPlaceDetails(null) // Clear any previous place details
    onManualEntry()
  }
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen || !predictions.length) return
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % totalItems)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev - 1 + totalItems) % totalItems)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex < predictions.length) {
          handleSelectPlace(predictions[highlightedIndex])
        } else {
          // Last item is "Fill in manually"
          handleManualEntry()
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsDropdownOpen(false)
        break
    }
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <StepHeader
        title={t('resources.signup.place_search.title')}
        subtitle={t('resources.signup.place_search.subtitle')}
        onBack={onBack}
      />

      {/* Form content */}
      <div className="flex flex-col gap-6">
        {/* Search input */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="business-search" className="text-sm font-medium text-[#181d27]">
              {t('resources.signup.place_search.label')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <SearchMdIcon className="h-5 w-5 text-[#a4a7ae]" />
              </div>
              <input
                id="business-search"
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setIsDropdownOpen(true)
                  setSelectedPlace(null)
                  setHighlightedIndex(0)
                }}
                onFocus={() => setIsDropdownOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={t('resources.signup.place_search.placeholder')}
                className="block w-full rounded-lg border border-[#d5d7da] bg-white py-2.5 pl-[42px] pr-10 text-base font-medium text-[#181d27] placeholder:text-[#717680] placeholder:font-normal focus:border-[#151515] focus:outline-none focus:ring-0 focus:shadow-[inset_0_0_0_1px_#151515]"
                autoComplete="off"
              />
              {isLoading && query.length >= 3 && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#d5d7da] border-t-[#181d27]" />
                </div>
              )}
            </div>
          </div>

          {/* Dropdown */}
          {isDropdownOpen && query.length >= 3 && (
            <div className="absolute z-10 mt-1.5 w-full rounded-lg border border-[rgba(0,0,0,0.08)] bg-white py-1 shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03),0px_2px_2px_-1px_rgba(10,13,18,0.04)]">
              {isLoading ? (
                <div className="px-3.5 py-2.5 text-base text-[#535862]">{t('resources.signup.place_search.searching')}</div>
              ) : (
                <>
                  {predictions.length ? (
                    <ul ref={listRef} className="max-h-60 overflow-auto">
                      {predictions.map((place, index) => (
                        <li key={place.place_id} className="px-1.5 py-px">
                          <button
                            type="button"
                            onClick={() => handleSelectPlace(place)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            className={cn(
                              'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2.5 text-left text-base leading-6 hover:bg-[#fafafa]',
                              highlightedIndex === index && 'bg-[#fafafa]'
                            )}
                          >
                            <span className="font-medium text-[#181d27] shrink-0">
                              {place.structured_formatting.main_text}
                            </span>
                            <span className="text-[#535862] truncate">
                              {place.structured_formatting.secondary_text}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-3.5 py-2.5 text-base text-[#535862]">
                      {t('resources.signup.place_search.no_results')}
                    </div>
                  )}
                  {/* Manual entry link inside dropdown */}
                  <div className="px-1.5 py-px">
                    <button
                      type="button"
                      onClick={handleManualEntry}
                      onMouseEnter={() => setHighlightedIndex(predictions.length)}
                      className={cn(
                        'flex w-full cursor-pointer items-center justify-center gap-1 rounded-md px-2 py-2.5 text-base leading-6 hover:bg-[#fafafa]',
                        highlightedIndex === predictions.length && predictions.length > 0 && 'bg-[#fafafa]'
                      )}
                    >
                      <span className="text-[#181d27]">{t('resources.signup.place_search.not_found')}</span>
                      <span className="font-semibold text-[#368f8b]">{t('resources.signup.place_search.manual_entry')}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
